import React, { useState, useEffect, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { Avatar, Box, Typography, Popover, IconButton } from '@mui/material';
import { BiSort } from 'react-icons/bi';
import { formatDate } from '../../../../utils/dateUtils';
import { TableSkeleton } from '../../SkeletonLoader/SkeletonLoader';
import './SubjectRequestTable.css';
// import { axiosInstance } from "../../../../utils/tokenUtils";
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import noSubjectRequestAnimation from '../../../../assets/Admin/no_subject_request.json';

const StatusColor = {
  approval_pending: { color: '#EBBF37', bgcolor: '#FFFAEC', label: 'Pending' },
  approved: { color: '#14B334', bgcolor: '#F2FFF5', label: 'Accepted' },
  rejected: { color: '#E14949', bgcolor: '#FFF2F2', label: 'Rejected' },
};

const LevelToTeachColor = {
  // Academic levels
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'UN',
    value2: 'University',
  },
  high_school: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'HS',
    value2: 'High School',
  },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'MS',
    value2: 'Middle School',
  },
  elementary: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'EL',
    value2: 'Elementary',
  },
  // Creative arts levels
  beginner: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'BG',
    value2: 'Beginner',
  },
  intermediate: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'IM',
    value2: 'Intermediate',
  },
  professional: {
    color: '#E14949',
    bgcolor: '#FFF2F2',
    value: 'PR',
    value2: 'Professional',
  },
};

const FilterLevelsValue = {
  University: { value: 'university' },
  'High School': { value: 'high_school' },
  'Middle School': { value: 'middle_school' },
  Elementary: { value: 'elementary' },
  Beginner: { value: 'beginner' },
  Intermediate: { value: 'intermediate' },
  Professional: { value: 'professional' },
};

function formatText(input) {
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

// Popover for level chips
const PopoverLevels = ({ levels, rowId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl) && levels && levels.length > 0;

  const handlePopoverOpen = event => {
    if (levels && levels.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <div
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      className="flex items-center cursor-pointer w-full"
    >
      <Box className="flex items-center gap-2">
        {levels &&
          levels.map((level, index) => {
            const levelColor = LevelToTeachColor[level];
            if (!levelColor) return null;

            return (
              <Box
                key={index}
                style={{
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 8px',
                  borderRadius: '20px',
                  backgroundColor: levelColor.bgcolor,
                  color: levelColor.color,
                  fontFamily: 'Poppins !important',
                  fontSize: '14px !important',
                  fontStyle: 'normal !important',
                  fontWeight: '400 !important',
                  lineHeight: 'normal !important',
                }}
              >
                <Typography className="level-text">{levelColor.value}</Typography>
              </Box>
            );
          })}
      </Box>

      {levels && levels.length > 0 && (
        <Popover
          id={`level-popover-${rowId}`}
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              borderRadius: '8px',
              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-6px',
                left: '20px',
                width: '12px',
                height: '12px',
                backgroundColor: 'white',
                transform: 'rotate(45deg)',
                borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              },
            },
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={handlePopoverClose}
          disableRestoreFocus
          PaperProps={{
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
            sx: {
              pointerEvents: 'auto',
              minWidth: '150px',
            },
          }}
        >
          <Box
            sx={{
              p: 1,
              '& > :not(:last-child)': {
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            {levels &&
              levels.map((level, index) => {
                const levelColor = LevelToTeachColor[level];
                if (!levelColor) return null;

                return (
                  <Box
                    key={index}
                    style={{
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px 8px',
                      borderRadius: '20px',
                      backgroundColor: levelColor.bgcolor,
                      color: levelColor.color,
                      fontFamily: 'Poppins !important',
                      fontSize: '12px !important',
                      fontStyle: 'normal !important',
                      fontWeight: '400 !important',
                      marginBottom: '2px',
                    }}
                  >
                    <Typography className="level-text">{levelColor.value2}</Typography>
                  </Box>
                );
              })}
          </Box>
        </Popover>
      )}
    </div>
  );
};

export default function SubjectRequestTable({
  subjectRequestData,
  searchTerm,
  level,
  approvalStatus,
  selectedSubject,
  isLoading = false,
}) {
  const navigate = useNavigate();
  const [filtered, setFiltered] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', order: 'desc' });
  const hasReceivedDataRef = useRef(false);

  // Use parent loading state if provided, otherwise use internal loading
  const loading = isLoading || internalLoading;

  // Track if we've received data at least once
  useEffect(() => {
    if (isLoading) {
      hasReceivedDataRef.current = false;
    } else if (subjectRequestData !== null && subjectRequestData !== undefined) {
      hasReceivedDataRef.current = true;
    }
  }, [isLoading, subjectRequestData]);
  // const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  // const [selectedRequestId, setSelectedRequestId] = useState(null);
  // const [openApproveModal, setOpenApproveModal] = useState(false);
  // const [openRejectModal, setOpenRejectModal] = useState(false);

  // const handleMenuOpen = (event, requestId) => {
  //   event.stopPropagation();
  //   // setMenuAnchorEl(event.currentTarget);
  //   setSelectedRequestId(requestId);
  // };

  const handleCellClick = id => {
    const basePath = '/admin/subject-request-details/';
    navigate(`${basePath}${id}`);
  };

  // const handleMenuClose = () => {
  //   // setMenuAnchorEl(null);
  //   setSelectedRequestId(null);
  // };

  // const handleAction = async (status) => {
  //   try {
  //     await axiosInstance.patch(
  //       `/admin/subjects/requests/${selectedRequestId}`,
  //       { status }
  //     );
  //     setFiltered((prev) =>
  //       prev.map((item) =>
  //         item.id === selectedRequestId ? { ...item, status } : item
  //       )
  //     );
  //   } catch (error) {
  //     console.error("Error updating request:", error);
  //   } finally {
  //     handleMenuClose();
  //   }
  // };

  // Handle sort button click
  const handleSortClick = key => {
    setSortConfig(prevConfig => ({
      key: key,
      order: prevConfig.key === key && prevConfig.order === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Filter data client-side
  useEffect(() => {
    // Don't process if parent is still loading
    if (isLoading) {
      return;
    }

    setInternalLoading(true);

    // Start with a copy of the data
    let data = [...(subjectRequestData || [])];

    console.log('Original data:', data.length, 'items');
    console.log('Filter parameters:', { searchTerm, level, selectedSubject, approvalStatus });

    // First, apply all filters
    // Filter by search term (teacher name)
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      data = data.filter(item => {
        const fullName =
          `${item.teacher?.first_name || ''} ${item.teacher?.last_name || ''}`.toLowerCase();
        return fullName.includes(term);
      });
      console.log('After search filter:', data.length, 'items');
    }

    // Filter by level to teach
    if (level && level !== 'All') {
      data = data.filter(item => {
        const teacherLevels = item.teacher?.level_of_teaching || [];
        return teacherLevels.includes(level);
      });
      console.log('After level filter:', data.length, 'items');
    }

    // Filter by subject (selectedSubject is a string title)
    if (selectedSubject && selectedSubject !== 'All') {
      data = data.filter(item => {
        return item.subject?.title === selectedSubject;
      });
      console.log('After subject filter:', data.length, 'items');
    }

    // Filter by approval status
    if (approvalStatus && approvalStatus !== 'all') {
      data = data.filter(item => item.status === approvalStatus);
      console.log('After status filter:', data.length, 'items');
    }

    // Then, apply sorting based on sortConfig
    data.sort((a, b) => {
      if (sortConfig.key === 'date') {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return sortConfig.order === 'desc' ? dateB - dateA : dateA - dateB;
      } else if (sortConfig.key === 'experience') {
        const expA = parseInt(a.teacher?.years_of_experience) || 0;
        const expB = parseInt(b.teacher?.years_of_experience) || 0;
        return sortConfig.order === 'desc' ? expB - expA : expA - expB;
      }
      return 0;
    });

    console.log('Final filtered data:', data.length, 'items');

    setFiltered(data);
    setInternalLoading(false);
    // setPage(1); // Reset to first page when data changes
  }, [
    subjectRequestData,
    searchTerm,
    level,
    selectedSubject,
    approvalStatus,
    sortConfig,
    isLoading,
  ]);

  // const { items } = usePagination({
  //   count: Math.ceil(filtered.length / rowsPerPage),
  //   onChange: (e, val) => setPage(val),
  // });

  // const startIndex = (page - 1) * rowsPerPage;
  // const currentRows = filtered.slice(startIndex, startIndex + rowsPerPage);

  // const handleRowsPerPage = e => {
  //   setRowsPerPage(e.target.value);
  //   setPage(1);
  // };

  // Show skeleton if parent is loading or we haven't received data yet
  if (
    isLoading ||
    !hasReceivedDataRef.current ||
    subjectRequestData === null ||
    subjectRequestData === undefined
  ) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, width: '100%' }}>
        <TableSkeleton />
      </Box>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '8px',
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="paginated table">
          <TableHead
            sx={{
              backgroundColor: '#EEF4FF',
              '& th': {
                color: 'var(--Text, #14171F)',
                height: '25px',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: '500',
                lineHeight: 'normal',
                padding: '10px 16px',
              },
            }}
          >
            <TableRow>
              <TableCell align="left">Name</TableCell>
              <TableCell align="left">Level to Teach</TableCell>
              <TableCell align="left">Subject Requested</TableCell>
              <TableCell align="center">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Years of Experience
                  {/* <IconButton
                    size="small"
                    onClick={() => handleSortClick('experience')}
                    sx={{
                      padding: '2px',
                      color: 'var(--Text, #14171F)',
                      '&:hover': {
                        backgroundColor: 'rgba(20, 23, 31, 0.04)',
                      },
                    }}
                  >
                    <BiSort fontSize="small" />
                  </IconButton> */}
                </Box>
              </TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="left">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Submit Date
                  {/* <IconButton
                    size="small"
                    onClick={() => handleSortClick('date')}
                    sx={{
                      padding: '2px',
                      color: 'var(--Text, #14171F)',
                      '&:hover': {
                        backgroundColor: 'rgba(20, 23, 31, 0.04)',
                      },
                    }}
                  >
                    <BiSort fontSize="small" />
                  </IconButton> */}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                color: 'var(--Text, #14171F) !important',
                height: '25px !important',
                fontFamily: 'Poppins !important',
                fontSize: '14px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: 'normal !important',
                padding: '9px 16px',
              },
            }}
          >
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    textAlign: 'center',
                    padding: '60px 16px',
                    borderBottom: 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 200,
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Lottie
                        animationData={noSubjectRequestAnimation}
                        loop={true}
                        autoplay={true}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#6b7280',
                        textAlign: 'center',
                      }}
                    >
                      No Subject Request Found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(item => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    '& td': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: '#E9F1FF !important',
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => handleCellClick(item.id)}
                >
                  <TableCell>
                    <Box className="flex items-center gap-3 font-poppins text-sm font-normal leading-normal">
                      <Avatar src={item.teacher.profile_image} sx={{ width: 25, height: 25 }} />
                      <Typography className="name-text">
                        {item.teacher.first_name} {item.teacher.last_name}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    {item.teacher.level_of_teaching && item.teacher.level_of_teaching.length > 0 ? (
                      <PopoverLevels levels={item.teacher.level_of_teaching} rowId={item.id} />
                    ) : (
                      <Typography className="level-text">---</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography className="subject-text">{item.subject.title || '---'}</Typography>
                  </TableCell>
                  <TableCell align="center">{item.teacher.years_of_experience || '---'}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        backgroundColor: StatusColor[item.status]?.bgcolor,
                        color: StatusColor[item.status]?.color,
                        p: '4px 8px',
                        borderRadius: '20px',
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: StatusColor[item.status]?.color,
                        }}
                      />
                      <Typography className="status-text">
                        {StatusColor[item.status]?.label || item.status}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
